import React from "react";
import * as Yup from "yup";

import {
  getComuniSpurghoServiti,
  TranslatedText,
} from "../../../helpers/helpers";

import {
  CaseForms,
  CaseMutationType,
  CaseStepperType,
  TipologiaInterventoSpurgho,
} from "../../../helpers/enums";
import {
  getEvictionForMutation,
  SEND_RICHIESTA_SPURGHO,
} from "../../../helpers/mutations";
import PrivacyPolicy from "../../common/PrivacyPolicy/PrivacyPolicy";
import { validEmail, validPhone } from "../../forms/CustomSignUp/validations";

// Il model definisce la struttura dei dati
// formId è un identificatore del form
// formField contiene la definizione dei singoli campi
const model = {
  formId: CaseForms.RichiediSpurghiCase,
  formField: {
    businessName: {
      name: "businessName",
      label: (
        <TranslatedText i18nKey="cases.request_info.fields.nominativo3.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.richiedi_omaggio.nominativo.placeholder" />
      ),
      requiredErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.required" />
      ),
      type: "input",
    },
    landlinePhoneNumber: {
      name: "landlinePhoneNumber",
      label: (
        <TranslatedText i18nKey="cases.request_info.fields.telefono.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.request_info.fields.telefono.placeholder" />
      ),
      invalidErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.telefono.invalid" />
      ),
      type: "input",
    },
    mobilePhoneNumber: {
      name: "mobilePhoneNumber",
      label: (
        <TranslatedText i18nKey="cases.request_info.fields.cellulare.label.required" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.request_info.fields.cellulare.placeholder" />
      ),
      requiredErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.required" />
      ),
      invalidErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.cellulare.invalid" />
      ),
      type: "input",
    },
    email: {
      name: "email",
      label: (
        <TranslatedText i18nKey="cases.request_info.fields.email.label.required" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.request_info.fields.email.placeholder" />
      ),
      invalidErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.email.invalid" />
      ),
      requiredErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.required" />
      ),
      type: "input",
    },

    indirizzoPuf: {
      name: "indirizzoPuf",
      label: (
        <TranslatedText i18nKey="cases.request_info.fields.indirizzo.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.request_info.fields.indirizzo.placeholder" />
      ),
      requiredErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.required" />
      ),
      type: "input",
    },
    comuneSpurgho: {
      name: "comuneSpurgho",
      label: <TranslatedText i18nKey="cases.allacciamento.comune.label" />,
      placeholder: (
        <TranslatedText i18nKey="cases.allacciamento.comune.placeholder" />
      ),
      requiredErrorMsg: (
        <TranslatedText i18nKey="errors.registration.public.fc.required" />
      ),
      type: "suggestions",
      message: true,

      getItems: (setData) => {
        return getComuniSpurghoServiti()
          .then((items) => setData(items))
          .catch((error) => {
            console.log("Error fetching comuni serviti: ", error);
            setData([]);
          });
      },
      getSuggestions: (data) => {
        return data;
      },
      filter: (value, data) => {
        return data.filter((item) => {
          return (
            String(item.comune)
              .toLowerCase()
              .indexOf(String(value).toLowerCase()) > -1
          );
        });
      },
      onSelected: () => {},
      onBlur: (value, data, setValue) => {
        const [selected] = data.filter(
          (item) =>
            String(item.comune).toLowerCase() === String(value).toLowerCase()
        );
        selected ? setValue(selected.comune) : setValue("");
      },
    },
    fisicaCAP: {
      name: "fisicaCAP",
      label: (
        <TranslatedText i18nKey="cases.allacciamento.step1_2.fisicaCAP.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.allacciamento.step1_2.fisicaCAP.placeholder" />
      ),
      invalidErrorMsg: (
        <TranslatedText i18nKey="cases.request_info.errors.cap.invalid" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      type: "input",
    },
    type: {
      name: "type",
      label: (
        <TranslatedText i18nKey="cases.spurghi.tipologia_intervento.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.spurghi.tipologia_intervento.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="common.select" />,
      type: "select",
      options: [
        {
          label: "",
          value: "",
        },
        {
          label: (
            <TranslatedText
              i18nKey={"cases.enums.TipologiaInterventoSpurgho.PROGRAMMED"}
            />
          ),
          value: TipologiaInterventoSpurgho.PROGRAMMED,
        },
        {
          label: (
            <TranslatedText
              i18nKey={
                "cases.enums.TipologiaInterventoSpurgho.EMERGENCY_SERVICE"
              }
            />
          ),
          value: TipologiaInterventoSpurgho.EMERGENCY_SERVICE,
        },
      ],
    },
    note: {
      name: "note",
      label: (
        <TranslatedText i18nKey="cases.spurghi.descrizione_intervento.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.spurghi.descrizione_intervento.placeholder" />
      ),
      requiredErrorMsg: <TranslatedText i18nKey="cases.errors.required" />,
      type: "text",
    },

    //Fatturazione
    nominativoRagSocialeFatturazione: {
      name: "nominativoRagSocialeFatturazione",
      label: (
        <TranslatedText i18nKey="cases.richiedi_omaggio.nominativo.placeholder" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.richiedi_omaggio.nominativo.placeholder" />
      ),
      type: "input",
    },
    codFiscale: {
      name: "codFiscale",
      label: <TranslatedText i18nKey="cases.spurgho.codiceFiscale.label" />,
      placeholder: (
        <TranslatedText i18nKey="cases.allacciamento.step4_1.datiImmobile_codiceFiscale.label" />
      ),
      uppercase: true,
      type: "input",
    },
    codPIva: {
      name: "codPIva",
      label: (
        <TranslatedText i18nKey="cases.fognatura.step4_1.datiImmobile_partitaIva.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.fognatura.step4_1.datiImmobile_partitaIva.label" />
      ),
      type: "input",
    },
    indirizzoFatturazione: {
      name: "indirizzoFatturazione",
      label: (
        <TranslatedText i18nKey="cases.allacciamento.step7_1.domicialiazone.indirizzo.label" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.allacciamento.step7_1.domicialiazone.indirizzo.label" />
      ),
      type: "input",
    },
    comuneFatturazione: {
      name: "comuneFatturazione",
      label: (
        <TranslatedText i18nKey="cases.richiedi_omaggio.comune.placeholder" />
      ),
      placeholder: (
        <TranslatedText i18nKey="cases.richiedi_omaggio.comune.placeholder" />
      ),
      type: "input",
    },
    fisicaCAPFatturazione: {
      name: "fisicaCAPFatturazione",
      label: <TranslatedText i18nKey="cases.spurghi.fields.fisicaCap.label" />,
      placeholder: (
        <TranslatedText i18nKey="cases.spurghi.fields.fisicaCap.label" />
      ),
      type: "input",
    },
    consentMarketing: {
      name: "consentMarketing",
      label: <TranslatedText i18nKey="form.privacy.label.first_checkbox" />,
      type: "checkbox",
      optionalLabel: true,
    },
    consentProfiling: {
      name: "consentProfiling",
      label: <TranslatedText i18nKey="form.privacy.label.second_checkbox" />,
      type: "checkbox",
      optionalLabel: true,
    },
  },
};

const {
  formField: {
    businessName,
    landlinePhoneNumber,
    mobilePhoneNumber,
    email,
    indirizzoPuf,
    comuneSpurgho,
    fisicaCAP,
    type,
    note,
    nominativoRagSocialeFatturazione,
    codFiscale,
    codPIva,
    indirizzoFatturazione,
    comuneFatturazione,
    fisicaCAPFatturazione,
    consentMarketing,
    consentProfiling,
  },
} = model;

// initialValues contiene i valori iniziali del form, ne imposta anche i campi.
// Si può usare per popolare il form eventualmente.
const initialValues = {
  [businessName.name]: "",
  [landlinePhoneNumber.name]: "",
  [mobilePhoneNumber.name]: "",
  [email.name]: "",
  [indirizzoPuf.name]: "",
  [comuneSpurgho.name]: "",
  [fisicaCAP.name]: "",
  [type.name]: "",
  [note.name]: "",
  [nominativoRagSocialeFatturazione.name]: "",
  [codFiscale.name]: "",
  [codPIva.name]: "",
  [indirizzoFatturazione.name]: "",
  [comuneFatturazione.name]: "",
  [fisicaCAPFatturazione.name]: "",
  [consentMarketing.name]: false,
  [consentProfiling.name]: false,
};

// Per definire il validationSchema si usa un array di shape che indicano gli step.
// L'ordine degli step deve corrispondere a quello in layout.
const getValidationSchema = () => {
  return [
    Yup.object().shape({
      [businessName.name]: Yup.string().required(businessName.requiredErrorMsg),
      [landlinePhoneNumber.name]: Yup.string().matches(validPhone, {
        message: landlinePhoneNumber.invalidErrorMsg,
      }),
      [mobilePhoneNumber.name]: Yup.string()
        .required(mobilePhoneNumber.requiredErrorMsg)
        .matches(validPhone, {
          message: mobilePhoneNumber.invalidErrorMsg,
        }),
      [email.name]: Yup.string()
        .required(email.requiredErrorMsg)
        .matches(validEmail, {
          message: email.invalidErrorMsg,
        }),
      [indirizzoPuf.name]: Yup.string().required(indirizzoPuf.requiredErrorMsg),
      [comuneSpurgho.name]: Yup.string().required(
        comuneSpurgho.requiredErrorMsg
      ),
      [fisicaCAP.name]: Yup.number().required(
        <TranslatedText i18nKey="errors.registration.public.fc.required" />
      ),
      [type.name]: Yup.string().required(type.requiredErrorMsg),
      [note.name]: Yup.string().required(note.requiredErrorMsg),
    }),
  ];
};
// Il layout contiene informazioni di testo e di distribuzione sulle griglie
const getLayout = (privacyLink, privacyCtaLabel) => {
  return [
    {
      chapter: true,
      chapterName: "",
      title: "",
      fields: [
        {
          fieldName: null,
          text: (
            <h2 className="case-step-title">
              <TranslatedText i18nKey="cases.richiedi_omaggio.titles.title1" />
            </h2>
          ),
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: businessName.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: email.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: landlinePhoneNumber.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: mobilePhoneNumber.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: indirizzoPuf.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: comuneSpurgho.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: fisicaCAP.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: type.name,
          grid: {
            xs: 12,
            md: 6,
          },
        },
        {
          fieldName: note.name,
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: null,
          text: (
            <h2 className="case-step-title mt-5 mb-5">
              <TranslatedText i18nKey="cases.spurghi.billing.title" />
            </h2>
          ),
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: nominativoRagSocialeFatturazione.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: codFiscale.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: codPIva.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: indirizzoFatturazione.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: comuneFatturazione.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: fisicaCAPFatturazione.name,
          grid: {
            xs: 12,
            md: 6,
            lg: 4,
          },
        },
        {
          fieldName: null,
          text: (
            <>
              <h2 className="case-step-title mt-5 mb-5">
                <TranslatedText i18nKey="form.privacy.label.title_third_scenario" />
              </h2>
              <div className="text-header mb-3">
                <div>
                  <PrivacyPolicy
                    privacyLink={privacyLink}
                    privacyCtaLabel={privacyCtaLabel}
                    thirdScenario
                  />
                </div>
              </div>
            </>
          ),
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: consentMarketing.name,
          grid: {
            xs: 12,
          },
        },
        {
          fieldName: consentProfiling.name,
          grid: {
            xs: 12,
          },
        },
      ],
    },
  ];
};

// Contiene dettagli sul case form
const formDetails = {
  title: <TranslatedText i18nKey={"cases.spurghi.title"} />,
  description: null,
  summary: false,
  stepper: CaseStepperType.None,

  helpText: null,
  successText: (
    <div>
      <p>
        <TranslatedText i18nKey="cases.request_info.success_text" />
      </p>
    </div>
  ),
};

const translatedMapping = (payload) => {
  return {
    category: CaseForms.RichiediSpurghiCase,
    type: payload[type.name],
    businessName: payload[businessName.name],
    landlinePhoneNumber: payload[landlinePhoneNumber.name],
    mobilePhoneNumber: payload[mobilePhoneNumber.name],
    email: payload[email.name],
    note: payload[note.name],
    pufAddress: {
      city: payload[comuneSpurgho.name],
      street: payload[indirizzoPuf.name],
      postalCode: payload[fisicaCAP.name],
    },
    billing: {
      name: payload[nominativoRagSocialeFatturazione.name],
      fiscalCode: payload[codFiscale.name],
      vatNumber: payload[codPIva.name],
      city: payload[comuneFatturazione.name],
      street: payload[indirizzoFatturazione.name],
      postalCode: payload[fisicaCAPFatturazione.name],
      // billingCode: payload[codiceFatturazione.name],
    },
    agreement: {
      profiling: Boolean(payload[consentProfiling.name]),
      marketing: Boolean(payload[consentMarketing.name]),
    },
  };
};
const translatedInverseMapping = (payload) => {
  const errorsFields = payload.map(({ field, message }) => {
    return { field, message };
  });
  const mapping = {
    "req.businessName": businessName.name,
    "req.type": type.name,
    "req.landlinePhoneNumber": landlinePhoneNumber.name,
    "req.mobilePhoneNumber": mobilePhoneNumber.name,
    "req.email": email.name,
    "req.note": note.name,
    "req.pufAddress.city": comuneSpurgho.name,
    "req.pufAddress.street": indirizzoPuf.name,
    "req.pufAddress.postalCode": fisicaCAP.name,
    "req.billing.name": nominativoRagSocialeFatturazione.name,
    "req.billing.fiscalCode": codFiscale.name,
    "req.billing.vatNumber": codPIva.name,
    "req.billing.city": comuneFatturazione.name,
    "req.billing.street": indirizzoFatturazione.name,
    "req.billing.postalCode": fisicaCAPFatturazione.name,
    "req.agreement.profiling": consentProfiling.name,
    "req.agreement.marketing": consentMarketing.name,
  };

  return errorsFields.reduce((acc, item) => {
    acc[mapping[item.field]] = item.message;
    return acc;
  }, {});
};
const mutation = {
  type: CaseMutationType.Custom,
  mutation: SEND_RICHIESTA_SPURGHO,
  refetchQueries: getEvictionForMutation("simpleCase"),
};

const main = (
  excludeSteps,
  counter,
  signDigital,
  webviewPopup,
  webviewRedirectPopup,
  buttonMatricolaContatoreCase,
  privacyLink,
  privacyCtaLabel
) => {
  const layout = getLayout(privacyLink, privacyCtaLabel);
  return {
    model,
    validationSchema: getValidationSchema(),
    initialValues,
    translatedMapping,
    translatedInverseMapping,
    layout,
    formDetails,
    mutation,
  };
};

export default main;
